import PropTypes from 'prop-types'

import { faPlus } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useFilters } from '@domain-group/react-hooks'

import { Box, Button, DashboardModule } from '@domain-group/suit-theme'

import useProviderOrderCode from '@hooks/useProviderOrderCode'
import useProviderOrderCodes from '@hooks/useProviderOrderCodes'
import useSelector from '@hooks/useSelector'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import { sortArrayBy } from '@domain-group/javascript-utils'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import OrderCodeListItem from './OrderCodeListItem'

const buildPageFilterFields = (entities) => {
  const { services } = entities

  const style = {
    marginBottom: 16,
  }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Product Code or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        endpoint: 'service_products',
        entityKey: 'serviceProducts',
      },
      key: 'service_product_id',

    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        endpoint: 'provider_price_lists',
        entityKey: 'providerPriceLists',
      },
      key: 'provider_price_list_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      label: 'Service',
      values: sortArrayBy(Object.values(services), 'asc', 'title').map(service => ({
        key: service.id,
        label: service.title,
      })),
    },
  ]
}

const OrderCodes = (props) => {
  const { providerId } = props

  const filterPayload = useFilters()
  const {
    pageFilters,
  } = filterPayload

  const options = {
    filters: { ...pageFilters, provider_id: providerId },
    requestOptions: defaultRequestOptions.providerOrderCode,
    performHttpRequests: !!providerId,
  }

  const providerOrderCodesPayload = useProviderOrderCodes(options)
  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderOrderCodes,
    filteredProviderOrderCodesCount,
    hasFilteredProviderOrderCodes,
    loading,
  } = providerOrderCodesPayload

  const {
    callbacks: { createOrEditProviderOrderCode },
  } = useProviderOrderCode({ provider_id: providerId })

  const entities = useSelector(reduxState => reduxState.entities)

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-order-codes' }}
      contentBoxProps={{ flexDirection: 'column' }}
      title="Order Codes"
      loading={loading}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields(entities)}
        {...filterPayload}
      />

      <Box flexDirection="column">
        {!loading && !hasFilteredProviderOrderCodes && (
          <DashboardModule.BlankState>
            <DashboardModule.BlankState.Title>
              No Order Codes present
            </DashboardModule.BlankState.Title>

            <DashboardModule.BlankState.Paragraph>
              Please assign an Order Code below.
            </DashboardModule.BlankState.Paragraph>
          </DashboardModule.BlankState>
        )}

        {!loading && hasFilteredProviderOrderCodes && (
          <DashboardModule.ScrollableSection flexDirection="column" maxHeight={600}>
            {filteredProviderOrderCodes.map(providerOrderCode => (
              <OrderCodeListItem
                key={providerOrderCode.id}
                providerOrderCode={providerOrderCode}
              />
            ))}
          </DashboardModule.ScrollableSection>
        )}

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredProviderOrderCodesCount}
          loading={loading}
        >
          <DashboardModule.FooterRight>
            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => createOrEditProviderOrderCode()}
              size={['medium', 'small']}
              width={['100%', 'fit-content']}
            >
              Add Order Code
            </Button>
          </DashboardModule.FooterRight>
        </DashboardModule.LoadMoreFooter>
      </Box>
    </DashboardModule>
  )
}

OrderCodes.propTypes = {
  providerId: PropTypes.number.isRequired,
}

export default OrderCodes
